import { useState, useRef } from "react";

import { useInteractOutside } from "@react-aria/interactions";
import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import { IconButton, QueryBarGroupItem } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { KEYWORD_GROUP } from "app/components/compare/KeywordsQueryBar/constants";
import { KeywordSearchContainer } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarStyles";
import { PageOverlay } from "app/components/compare/WebsiteQueryBar/WebsiteQueryBar";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    type KeywordList,
    type KeywordListEntity,
    type KeywordListType,
    createAiTopicEntity,
    createKeywordGroupEntity,
} from "app/modules/demand-analysis/keyword-lists";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { useSharedKeywordGroups } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { allTrackers } from "app/services/track/track";
import { AutocompleteDemandAnalysisUnified } from "pro-features/components/AutocompleteDemandAnalysisUnified/AutocompleteDemandAnalysisUnified";
import type { IAiTopic } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DEMAND_ANALYSIS_HOME_STATE_NAME } from "../../shared/constants";

type AutocompleteItem = IAiTopic | IUserKeywordGroup;

function createKeywordListEntity(autocompleteItem: AutocompleteItem): KeywordListEntity {
    if ("Keywords" in autocompleteItem) {
        return createKeywordGroupEntity(
            { type: "KeywordGroup", keywordGroupId: autocompleteItem.Id },
            autocompleteItem.Name,
        );
    }

    if ("seed" in autocompleteItem) {
        return createAiTopicEntity(
            { type: "AiTopic", seed: autocompleteItem.seed, country: WORLDWIDE_COUNTRY_ID },
            autocompleteItem.seed,
        );
    }

    return {} as KeywordListEntity;
}

const AUTOCOMPLETE_ID = "@@AUTOCOMPLETE_ID";

const secondaryTextToTypeDict: Partial<Record<KeywordListType, string>> = {
    keywordGroup: KEYWORD_GROUP,
    aiTopic: "demand_analysis.autocomplete.ai_topic",
};

const iconToTypeDict: Partial<Record<KeywordListType, string>> = {
    keywordGroup: "nav-keyword-group",
    aiTopic: "search",
};

// Helper functions to get appropriate text and icon based on shared status
const getSecondaryText = (
    keywordListType: KeywordListType,
    sharedGroups?: { Id: string }[],
    keywordListId?: string,
): string => {
    if (keywordListType === "keywordGroup" && keywordListId) {
        const isShared = sharedGroups?.some((group) => group?.Id === keywordListId) ?? false;
        if (isShared) {
            return "demand.analysis.autocomplete.sharedList";
        }
    }
    return secondaryTextToTypeDict[keywordListType] ?? "";
};

const getIcon = (keywordListType: KeywordListType, sharedGroups?: { Id: string }[], keywordListId?: string): string => {
    if (keywordListType === "keywordGroup" && keywordListId) {
        const isShared = sharedGroups?.some((group) => group?.Id === keywordListId) ?? false;
        if (isShared) {
            return "group-shared";
        }
    }
    return iconToTypeDict[keywordListType] ?? "";
};

interface IProps {
    isActive: boolean;
    country?: number;
    currentKeywordList?: KeywordList;
    selectedKeywordListIds: string[];
    onKeywordListAdd: (keywordListEntity: KeywordListEntity) => void;
    onKeywordListChange: (prevKeywordListEntity: KeywordListEntity, nextKeywordListEntity: KeywordListEntity) => void;
    onKeywordListRemove: (keywordListEntity: KeywordListEntity) => void;
    onOpen: (activeItemId: string) => void;
    onClose: () => void;
}

type IStateAndParams = {
    country?: number;
};

export const KeywordListsComparisonNavQueryBarItem = ({
    isActive,
    country = WORLDWIDE_COUNTRY_ID,
    currentKeywordList,
    selectedKeywordListIds,
    onKeywordListAdd,
    onKeywordListChange,
    onKeywordListRemove,
    onOpen,
    onClose,
}: IProps) => {
    const t = useTranslation();
    const translate = i18nFilter();
    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const [shouldKeepActive, setShouldKeepActive] = useState(false);
    const isCompare = selectedKeywordListIds.length > 1;
    const keywordsGroupsToFilterFromAutocomplete = selectedKeywordListIds.filter(
        (keywordId) => keywordId !== currentKeywordList?.id,
    );
    const { data: sharedGroups } = useSharedKeywordGroups();
    const stateAndParams = useCurrentStateAndParams<IStateAndParams>();
    const resolvedCountryId = stateAndParams.params.country ?? country;

    const handleButtonClick = () => {
        onOpen(currentKeywordList?.id ?? AUTOCOMPLETE_ID);
        allTrackers.dispatchTrackEvent("Compare", "open", "Header");
    };

    const handleBarItemClick = () => {
        onOpen(currentKeywordList?.id ?? AUTOCOMPLETE_ID);
    };

    const handleClose = () => {
        if (!shouldKeepActive) {
            onClose();
        }
    };

    const handleAutocompleteItemClick = (autocompleteItem: AutocompleteItem) => {
        const newEntity = createKeywordListEntity(autocompleteItem);

        if (currentKeywordList !== undefined) {
            onKeywordListChange(currentKeywordList, newEntity);
        } else {
            allTrackers.dispatchTrackEvent("Select keyword list", "click", "Auto complete", {
                kwListType: "Keyword list",
                kwListId: newEntity.id,
            });
            onKeywordListAdd(newEntity);
        }
    };

    const handleKeyboardSelect = (autocompleteItem: AutocompleteItem) => {
        const newEntity = createKeywordListEntity(autocompleteItem);

        if (currentKeywordList !== undefined) {
            onKeywordListChange(currentKeywordList, newEntity);
        } else {
            onKeywordListAdd(newEntity);
        }
    };

    const handleKeywordRemoveClick = () => {
        if (currentKeywordList) {
            onKeywordListRemove(currentKeywordList);
        }
    };

    const handleDeleteGroup = async (deletedGroup: { Id: string }) => {
        if (!isCompare) {
            return await proNavigator.go(DEMAND_ANALYSIS_HOME_STATE_NAME);
        }

        if (currentKeywordList && currentKeywordList.id === deletedGroup.Id) {
            onKeywordListRemove(currentKeywordList);
        }
    };

    useInteractOutside({ ref: autocompleteContainerRef, onInteractOutside: handleClose });

    return (
        <Box position="relative">
            {isActive ? (
                <div ref={autocompleteContainerRef}>
                    <KeywordSearchContainer className="QueryBarAutoComplete">
                        <AutocompleteDemandAnalysisUnified
                            showGoToMonitorKeywordListButton
                            truncateResultsOnBodyClick
                            isPrimaryEntitySelection
                            countryId={resolvedCountryId}
                            selectedValue={currentKeywordList?.name ?? ""}
                            keywordsGroupsToFilterFromAutocomplete={keywordsGroupsToFilterFromAutocomplete}
                            onClick={handleAutocompleteItemClick}
                            onKeyboardSelect={handleKeyboardSelect}
                            onGroupShareModalOpened={() => setShouldKeepActive(true)}
                            onGroupShareModalClosed={() => {
                                setShouldKeepActive(false);
                                onClose();
                            }}
                            onGroupEditorModalOpened={() => setShouldKeepActive(true)}
                            onGroupEditorModalClosed={() => {
                                setShouldKeepActive(false);
                                onClose();
                            }}
                            onGroupDelete={handleDeleteGroup}
                            handleSave={noop}
                            shouldConfirmDeleteKeywordList
                            autocompleteProps={{
                                placeholder: translate(
                                    "marketintelligence.marketresearch.keywordmarket.home.search.placeholder.searchfor",
                                ),
                                isFocused: true,
                                autoFocus: true,
                                autoSelectInputOnFocus: true,
                                preventTruncateUnlessForced: true,
                            }}
                        />
                    </KeywordSearchContainer>
                    <div onClick={handleClose}>
                        <PageOverlay
                            isOpen
                            hideSidebarOverlay
                        />
                    </div>
                </div>
            ) : null}

            {currentKeywordList ? (
                <QueryBarGroupItem
                    isCompare={isCompare}
                    hideRemoveButton={!isCompare}
                    text={currentKeywordList.name}
                    secondaryText={t(getSecondaryText(currentKeywordList.type, sharedGroups, currentKeywordList.id))}
                    iconName="arrow"
                    iconSize="10px"
                    icon={getIcon(currentKeywordList.type, sharedGroups, currentKeywordList.id)}
                    badgeColor={currentKeywordList.color}
                    onItemClick={handleBarItemClick}
                    onButtonClick={handleKeywordRemoveClick}
                />
            ) : (
                <IconButton
                    className="QueryBar-compare-button"
                    onClick={handleButtonClick}
                    iconName="add"
                >
                    {t("keyword.query.bar.compare.button.text")}
                </IconButton>
            )}
        </Box>
    );
};

KeywordListsComparisonNavQueryBarItem.autoCompleteId = AUTOCOMPLETE_ID;
